// CTV-682: "Share your link" — the sheet a manager gives a sales rep. Revised
// against station feedback (KITV, Aug 2026), which kept the headline and
// rewrote the body; the version it replaced is kept in ./originals.
//
// What the revision changed:
//   • The subhead states the value directly instead of characterising the
//     leads as ones the rep can't be bothered with.
//   • Step 1 names the artefact ("Your personalized link") and tells the rep
//     where to put it.
//   • Step 2 makes the segmentation argument explicitly: advertisers run
//     themselves, which buys the rep time for bigger opportunities. Channel-
//     conflict practice calls this compensation neutrality — the rep's
//     attainment shouldn't depend on which channel closed the business.
//
// The headline deliberately does NOT name the fear. An earlier draft opened
// "It doesn't replace you", which plants the idea it was trying to dispel.

import React from 'react';
import type { PublisherConfig } from '../../../../../types/selfService';
import MaterialSheet from '../../MaterialSheet';
import { storefrontHref, storefrontUrl } from '../../storefrontUrl';
import { displayedName } from '../../../../../data/publisherConfigs';

const RepOnePagerV2: React.FC<{ config: PublisherConfig; refCode?: string | null }> = ({
  config,
  refCode,
}) => {
  const name = displayedName(config);

  return (
    <MaterialSheet
      config={config}
      variant="memo"
      kicker={
        <>
          <span className="msheet-tag">Internal</span>
          <br />
          For our sales team
        </>
      }
      footNote={`© ${name} · Internal sales enablement`}
    >
      <div className="msheet-eyebrow">For the {name} sales team</div>
      <h1>
        <span>Share your link.</span>
        <em>Collect the commission.</em>
      </h1>
      <p className="msheet-sub">
        Your accounts stay yours.{' '}
        <b>This turns smaller opportunities into revenue, without adding to your workload.</b>
      </p>

      <div className="msheet-mid">
        <div className="msheet-steps msheet-steps--rows">
          <div className="msheet-step">
            <div className="msheet-num">1</div>
            <h3>Your personalized link</h3>
            <p>
              Add it to your emails and business cards. Advertisers who use it get bonus ads.
            </p>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">2</div>
            <h3>Advertisers build, buy, and manage their own campaigns</h3>
            <p>No extra meetings. No extra admin. More time for bigger opportunities.</p>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">3</div>
            <h3>You get the commission</h3>
            <p>Every campaign from your link is credited to you.</p>
          </div>
        </div>

        <div className="msheet-why">
          <div>
            <div className="msheet-why-label">What doesn&rsquo;t change</div>
            <h4>Everything you sell today</h4>
            <p>Your relationships, your direct buys, your rate card. All untouched.</p>
          </div>
          <div>
            <div className="msheet-why-label">What&rsquo;s new</div>
            <h4>The leads you had to skip</h4>
            <p>The advertiser too small to justify a meeting can now book themselves.</p>
          </div>
        </div>

        <div className="msheet-chips">
          <div className="msheet-chip msheet-chip--hi">Your own referral code</div>
          <div className="msheet-chip">Commission on every campaign</div>
          <div className="msheet-chip">Track your own numbers</div>
        </div>

        <div className="msheet-cta">
          <div className="msheet-cta-text">
            <div className="msheet-cta-label">Next step</div>
            <div className="msheet-cta-main">
              <strong>Get your referral link.</strong>
              <a href={storefrontHref(config.slug, refCode)} target="_blank" rel="noreferrer">
                {storefrontUrl(config.slug, refCode ?? 'YOURCODE')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </MaterialSheet>
  );
};

export default RepOnePagerV2;
